/**
 * Devoluciones de consignaciones — consulta de devolucioncxp.
 *
 * Lista las devoluciones registradas por rango de fechas y consignante, con
 * reimpresión del comprobante (individual o el conduce completo del lote),
 * export a Excel y el REINGRESO de una devolución hecha por error: recrea el
 * motor en inventario desde el snapshot guardado, desmarca la línea del
 * conduce y suma su valor de nuevo a la cabecera (backend
 * /api/consignaciones_reingresardevolucion, permiso de borrar).
 */
import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import RestoreOutlinedIcon from "@mui/icons-material/RestoreOutlined";
import { useMaterialUIController, useGlobalMDAlert } from "context";
import { puedeAccionPrograma } from "lib/seguridad/permisosPorModulo";
import { FP, PageHeader, fpInputSx, fpDataGridSx, StatusPill } from "components/financepro";
import { fechaDMY, useApi } from "lib/api";
import {
  imprimirDevolucionCxp,
  imprimirConduceDevolucion,
} from "modules/cuentasxpagar/impresionfacturacxp";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const hoyISO = () => new Date().toISOString().slice(0, 10);
const inicioAnoISO = () => `${new Date().getFullYear()}-01-01`;

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin datos — procese la consulta
      </Typography>
    </Stack>
  );
}

export default function DevolucionesConsignaciones() {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const [controller] = useMaterialUIController();
  const { roleDetalles } = controller;
  // Mismo par de permisos que acepta el backend para devolver/reingresar.
  const puedeReingresar =
    puedeAccionPrograma(roleDetalles, "consignaciones", "borrar") ||
    puedeAccionPrograma(roleDetalles, "ares8550", "borrar");

  const [fecha1, setFecha1] = useState(inicioAnoISO());
  const [fecha2, setFecha2] = useState(hoyISO());
  const [consignantes, setConsignantes] = useState([]);
  const [consignante, setConsignante] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [procesado, setProcesado] = useState(false);
  // Membrete (con logo) para las reimpresiones; lo devuelve el listado.
  const [compania, setCompania] = useState(null);

  const [reingreso, setReingreso] = useState(null); // fila a reingresar | null
  const [motivoReingreso, setMotivoReingreso] = useState("");
  const [reingresando, setReingresando] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { ok, data } = await post("/api/cxp_suplidores", {});
        if (ok) setConsignantes(data.suplidores || []);
      } catch (err) {
        setConsignantes([]);
      }
    })();
  }, []);

  const procesar = async () => {
    setLoading(true);
    try {
      const { ok, data } = await post("/api/consignaciones_devoluciones", {
        fechadesde: fecha1,
        fechahasta: fecha2,
        ...(consignante ? { suplidor: consignante.id } : {}),
      });
      if (ok) {
        setRows(data.devoluciones || []);
        if (data.compania) setCompania(data.compania);
        setProcesado(true);
      } else {
        showAlert({ message: data.Error || "No se pudo consultar", color: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  const reimprimir = (row) => {
    if (row.lote) {
      const grupo = rows.filter((r) => r.lote === row.lote);
      if (grupo.length > 1) {
        imprimirConduceDevolucion(grupo, compania);
        return;
      }
    }
    imprimirDevolucionCxp(row, compania);
  };

  const confirmarReingreso = async () => {
    if (!reingreso) return;
    setReingresando(true);
    try {
      const { ok, data } = await post("/api/consignaciones_reingresardevolucion", {
        id: reingreso.id,
        motivo: motivoReingreso.trim(),
      });
      if (ok) {
        showAlert({ message: data.ok || "Devolución reingresada", color: "success" });
        setReingreso(null);
        setMotivoReingreso("");
        procesar();
      } else {
        showAlert({ message: data.Error || "No se pudo reingresar", color: "error" });
      }
    } catch (err) {
      showAlert({ message: `Error de red: ${err.message}`, color: "error" });
    } finally {
      setReingresando(false);
    }
  };

  const subtitulo = `Del ${fechaDMY(fecha1)} al ${fechaDMY(fecha2)}`;

  const exportar = () => {
    const hoja = XLSX.utils.aoa_to_sheet([
      ["Devoluciones de Consignaciones"],
      [subtitulo],
      [],
      [
        "Fecha",
        "Folio",
        "Fact#",
        "Consignante",
        "Chasis",
        "Marca",
        "Modelo",
        "Color",
        "Valor",
        "Motivo",
        "Recibido por",
        "Registrada por",
        "Estado",
      ],
      ...rows.map((d) => [
        d.fecha,
        d.lote ?? d.id,
        d.nfactura,
        d.suplidornombre,
        d.chasis,
        d.marca,
        d.modelo,
        d.colore,
        d.valor,
        d.motivo,
        d.recibidopor,
        d.usuario,
        d.reingresada ? `Reingresada (${d.reingresadapor} ${d.fechareingreso || ""})` : "Vigente",
      ]),
    ]);
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, "Devoluciones");
    XLSX.writeFile(libro, `DevolucionesConsignaciones_${hoyISO()}.xlsx`);
  };

  const columns = [
    { field: "fecha", headerName: "Fecha", width: 100 },
    {
      field: "lote",
      headerName: "Folio",
      width: 80,
      valueGetter: (value, row) => value ?? row.id,
    },
    { field: "nfactura", headerName: "Fact#", width: 100 },
    { field: "suplidornombre", headerName: "Consignante", minWidth: 170, flex: 1 },
    { field: "chasis", headerName: "Chasis", minWidth: 150, flex: 1 },
    { field: "marca", headerName: "Marca", width: 100 },
    { field: "modelo", headerName: "Modelo", width: 100 },
    {
      field: "valor",
      headerName: "Valor",
      width: 110,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    { field: "motivo", headerName: "Motivo", minWidth: 140, flex: 1 },
    { field: "recibidopor", headerName: "Recibido por", width: 130 },
    {
      field: "reingresada",
      headerName: "Estado",
      width: 120,
      renderCell: (params) =>
        params.value ? (
          <Tooltip title={`Por ${params.row.reingresadapor} el ${params.row.fechareingreso || ""}`}>
            <span>
              <StatusPill label="Reingresada" variant="info" />
            </span>
          </Tooltip>
        ) : (
          <StatusPill label="Vigente" variant="danger" />
        ),
    },
    {
      field: "__acciones",
      headerName: "Acciones",
      width: 110,
      sortable: false,
      disableColumnMenu: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip
            title={params.row.lote ? "Reimprimir conduce del lote" : "Reimprimir comprobante"}
          >
            <IconButton
              size="small"
              onClick={() => reimprimir(params.row)}
              sx={{ color: FP.color.navy }}
            >
              <PrintOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {puedeReingresar && params.row.reingresable && (
            <Tooltip title="Reingresar (deshacer la devolución)">
              <IconButton
                size="small"
                onClick={() => setReingreso(params.row)}
                sx={{ color: FP.color.success }}
              >
                <RestoreOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ];

  const totalValor = rows.reduce((s, r) => s + (parseFloat(r.valor) || 0), 0);

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Devoluciones de Consignaciones"
        subtitle="Devoluciones registradas, reimpresión de comprobantes y reingreso de errores"
      />
      <Box sx={{ p: { xs: 2, md: 3 }, display: "flex", flexDirection: "column", gap: 2.5 }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Desde"
                value={fecha1}
                onChange={(e) => setFecha1(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                value={fecha2}
                onChange={(e) => setFecha2(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                options={consignantes}
                value={consignante}
                onChange={(e, v) => setConsignante(v)}
                getOptionLabel={(o) => o?.descrip || ""}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Consignante (todos)"
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={1.2} justifyContent="flex-end" flexWrap="wrap">
                <Button
                  startIcon={
                    loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                  }
                  disabled={loading}
                  onClick={procesar}
                  sx={btnSolid(FP.color.navy, FP.color.navySoft)}
                >
                  Procesar
                </Button>
                <Button
                  startIcon={<FileDownloadOutlinedIcon />}
                  disabled={!rows.length}
                  onClick={exportar}
                  sx={btnOutline(FP.color.accentBlue)}
                >
                  Excel
                </Button>
              </Stack>
            </Grid>
          </Grid>
          {procesado && (
            <Typography sx={{ mt: 1.5, fontSize: "0.82rem", color: FP.color.textSecondary }}>
              {rows.length} devoluciones · Total RD$ {fmtMoney(totalValor)} · {subtitulo}
            </Typography>
          )}
        </Box>

        <Box sx={{ ...sectionBox, p: { xs: 1, sm: 1.5 } }}>
          <Box sx={{ width: "100%", overflowX: "auto" }}>
            <DataGrid
              rows={rows}
              columns={columns}
              getRowId={(row) => row.id}
              loading={loading}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              disableRowSelectionOnClick
              rowHeight={42}
              sx={{ minWidth: 900, height: 560, ...fpDataGridSx }}
              autoHeight={false}
            />
          </Box>
        </Box>
      </Box>

      {/* Confirmación de reingreso */}
      <Dialog
        open={!!reingreso}
        onClose={() => (reingresando ? null : setReingreso(null))}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Reingresar devolución</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.92rem" }}>
            El chasis <b>{reingreso?.chasis}</b> volverá al inventario tal como estaba, su línea del
            conduce dejará de estar devuelta y el valor (RD$ {fmtMoney(reingreso?.valor)}) se sumará
            de nuevo a la consignación de <b>{reingreso?.suplidornombre}</b>.
          </Typography>
          <TextField
            fullWidth
            label="Motivo del reingreso"
            value={motivoReingreso}
            onChange={(e) => setMotivoReingreso(e.target.value)}
            disabled={reingresando}
            sx={{ ...fpInputSx, mt: 2 }}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setReingreso(null)}
            disabled={reingresando}
            sx={btnOutline(FP.color.textSecondary)}
          >
            Cancelar
          </Button>
          <Button
            startIcon={
              reingresando ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <RestoreOutlinedIcon />
              )
            }
            disabled={reingresando}
            onClick={confirmarReingreso}
            sx={btnSolid(FP.color.success, FP.color.successDark)}
          >
            {reingresando ? "Reingresando…" : "Reingresar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
